// How old the ZTM picture is, said out loud (Decision 37, #83; SPEC-redesign
// §5.3, §7.2). Pure and DOM-free: the kreska paints what this returns, and
// nothing here knows about an element.
//
// The reading is a **state**, not a counter. `sentence` is what the live region
// announces and changes only when the state does; `short` is what the eye reads
// and may tick every second behind `aria-hidden` (#72).
import type { Snapshot } from '../data/data.ts';
import { clockLabel, LIVE_S } from '../shared/format.ts';

/** Everything the reading is derived from. A subset of `Snapshot`, so a test needs no items. */
export type FreshnessInput = Pick<Snapshot, 'health' | 'ztmUpdatedAt' | 'ztmFeedTs' | 'serverReachable'>;

/**
 * How old a source's data is, once the two questions above it are answered:
 * whether our own server replied at all, and whether this source has ever
 * answered. Every surface that speaks about age asks this and nothing else, so
 * the Warstwy row, the picker header, the axis notes and the kreska can never
 * draw the line in two places (#218).
 */
export type Band =
  /** answering, and the picture is current: nothing is said about it */
  | 'live'
  /** answering, 90 s–10 min: the age is said, quietly, and no source is named */
  | 'ageing'
  /** answering, past 10 min: the age plus „Czekamy na odpowiedź {źródło}." */
  | 'old'
  /** not answering, and we hold something it gave us earlier */
  | 'stale'
  /** not answering, and it never gave us anything (§9.3 row 19) */
  | 'dead';

type FreshnessKind =
  /** answering, and the picture is current */
  | 'live'
  /** no answer of any kind yet — and this must never read like `dead` (D-2) */
  | 'starting'
  /** we hold a picture, it is getting old, ZTM has not refreshed it */
  | 'ageing'
  /** we hold a picture and ZTM stopped answering: it is the last one there is */
  | 'stale'
  /** ZTM answered nothing, ever, and has given up (§9.3 row 19) */
  | 'dead'
  /** our own server is unreachable (§9.3 row 20) */
  | 'offline';

/** `●` filled, `◌` hollow, `⊘` a struck-through sign. Colour is never the only signal (§8). */
type Dot = 'live' | 'hollow' | 'sign';

export type Freshness = {
  kind: FreshnessKind;
  /** What the live region announces: the state, never the number (#72). */
  sentence: string;
  /** What the eye reads beside the dot. Ticking only while `ticking` is true. */
  short: string;
  dot: Dot;
  /** Brick means "this is wrong", and it is never the only signal. */
  brick: boolean;
  /** The `short` above counts seconds, so the kreska repaints it each second. */
  ticking: boolean;
};

/**
 * Under this a snapshot is the live picture and reads in seconds; over it, it
 * is dated with the hour it was stamped. It is `LIVE_S` from `src/shared/`, the
 * one line the position fix is measured against too (#218); the bands built on
 * it live only here, and `voices.ts`, `layers.ts` and `picker.ts` ask them.
 */
export const LIVE_MS = LIVE_S * 1000;

/**
 * Past this a source that is still answering has kept us waiting long enough to
 * say so out loud („Czekamy na odpowiedź Aquanetu."). It is a wait and never a
 * fault: whether a source is *down* is `/api/health`'s verdict, never a clock
 * of ours, so nothing here may call a source that answers „nie odpowiada".
 */
export const OLD_MS = 600_000;

/**
 * The one place the bands are drawn (#218).
 *
 * `age` is how long ago the SOURCE last gave us data — its own stamp, not the
 * moment our server last asked (#75) — and `null` means it never did. `healthy`
 * is `/api/health`'s verdict on the source.
 *
 * Precondition: a healthy source with nothing fetched yet is `starting`, which
 * is a state and not an age at all (§9.3 row 16), and every caller answers that
 * before it gets here. Asking anyway is a bug worth hearing about rather than a
 * band quietly invented for it.
 */
export function band(age: number | null, healthy: boolean): Band {
  if (age === null) {
    if (healthy) throw new Error('band: a healthy source with nothing fetched is `starting`, not an age');
    return 'dead';
  }
  if (!healthy) return 'stale';
  if (age < LIVE_MS) return 'live';
  return age < OLD_MS ? 'ageing' : 'old';
}

/**
 * The hour the ZTM picture was stamped with: ZTM's own header, and our refresh
 * only when the wire gave us no header to believe (#75).
 */
export const ztmStamp = (input: Pick<Snapshot, 'ztmFeedTs' | 'ztmUpdatedAt'>): number =>
  input.ztmFeedTs ?? input.ztmUpdatedAt;

/**
 * ZTM has given up. The single worst lie in the prototype is that a dead source
 * and a starting one render byte-identically, for ever (D-2): a source that has
 * given up may not claim to be connecting, and one that never answered at all
 * may not be dated with an hour it never reached. `updatedAt` is the health's
 * clock — the last moment ZTM itself answered (row 18).
 */
function down(updatedAt: number): Freshness {
  if (updatedAt === 0) {
    return {
      kind: 'dead',
      sentence: 'ZTM nie odpowiada',
      short: '—',
      dot: 'sign',
      brick: true,
      ticking: false,
    };
  }
  const at = clockLabel(updatedAt);
  return {
    kind: 'stale',
    // The age first, the fault second: what the resident acts on is how old
    // the positions are, and the source is named because it is the one thing
    // that will not fix itself in a minute (Decision 37).
    sentence: `Dane z ${at}, ZTM nie odpowiada.`,
    short: at,
    dot: 'sign',
    brick: true,
    ticking: false,
  };
}

/**
 * Whether ZTM is answering is the **server's** verdict, read from
 * `/api/health` through `Snapshot.health.ztm`, never a timer of ours. The
 * clock decides the age, `/api/health` decides whether a source may be named:
 * a source that is merely slow is a wait, never an outage.
 */
export function freshness(input: FreshnessInput, now: number): Freshness {
  // A failed request to our own server is the one thing that reads „brak
  // połączenia z serwerem", and it means what it says (SPEC-foundation §3.4).
  // It outranks every source's own state: with no answer from our server, the
  // health block is the last one we were handed, about a question nobody can
  // answer right now. One gap for every voice, not four complaints (row 20).
  if (!input.serverReachable) {
    return {
      kind: 'offline',
      sentence: 'brak połączenia z serwerem',
      short: '—',
      dot: 'sign',
      brick: true,
      ticking: false,
    };
  }

  const ztm = input.health.ztm;

  // No refresh has succeeded yet and none has failed. There is no ceiling on
  // this: a slow first refresh is a slow first refresh however long it takes
  // (SPEC-foundation §3.4). `ok` with nothing on the endpoint yet is one poll
  // of skew and reads the same way — it is not a failure either (row 16).
  if (ztm.state === 'starting' || (ztm.state === 'ok' && input.ztmUpdatedAt === 0)) {
    return {
      kind: 'starting',
      sentence: 'łączę się…',
      short: '…',
      dot: 'hollow',
      brick: false,
      ticking: false,
    };
  }

  if (ztm.state === 'down') return down(ztm.updatedAt);

  // Answering. All that is left is how old the picture we hold is — and that is
  // ZTM's own clock, not ours. Our server bumps `ztmUpdatedAt` on every
  // successful poll, so a ZTM answering 200 with a header that stands still
  // would read „na żywo" for ever off that number. `ztmFeedTs` standing still
  // is the only thing that says the data did. With no header to believe, our
  // refresh is a worse answer than ZTM's and a better one than none.
  const age = Math.max(0, now - ztmStamp(input));
  if (band(age, true) === 'live') {
    const secs = Math.round(age / 1000);
    return {
      kind: 'live',
      // The prototype announces „na żywo, {n} s temu", which is a counter and
      // would be spoken again every second — #72 in one string. The seconds
      // stay on screen, in `short`, behind `aria-hidden`.
      sentence: 'na żywo',
      short: `${secs} s`,
      dot: 'live',
      brick: false,
      ticking: true,
    };
  }
  // ZTM is fine and has simply not refreshed us yet (row 17). Data that is four
  // minutes old is not wrong, it is four minutes old, so the reading leaves
  // brick to the states that have nothing to show — and it says the hour the
  // source stamped rather than a minute count the resident has to subtract
  // from the „teraz" two centimetres above it (Decision 37, variant A).
  //
  // `ageing` and `old` read identically here on purpose: the ZTM gap band
  // beside this reading already carries „Czekamy na odpowiedź ZTM." from 90 s
  // (`ztmGap` in `voices.ts`), and one screen says that once.
  const at = clockLabel(ztmStamp(input));
  return {
    kind: 'ageing',
    sentence: `dane z ${at}`,
    short: at,
    dot: 'hollow',
    brick: false,
    ticking: false,
  };
}
